import Portal from "@/components/portal";
import classNames from "classnames";
import { useEffect, useMemo } from "react";
import { useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import RoundActionButton from "@/components/ui/buttons/round-action-button";
import HighlightPlayer from "./highlights-player";
import StoreInfoModal from "./store-info-modal";
import ProductsModal from "./products-modal";
import ShareHighlightModal from "./share-highlight-modal";
import { useModals } from "@/components/hooks/useModals";
import { HighlightInterface, ProductItemInterface, StoreInterface } from "@/assets/interfaces";
import useLockPageScroll from "@/components/hooks/useLockPageScroll";
import CartContext from "@/contexts/cart-context";
import { computeItemAvailability } from "@/assets/js/utils/utils";
import usePreventFirstRun from "@/components/hooks/usePreventFirstRun";
import StorefrontContext from "@/contexts/storefront";
import { useRouter } from "next/router";
import CartContextV2 from "@/contexts/cart-context-v2";

export interface PlayerStageProps {
  show: boolean;
  toggle: () => void;
  initialHighlightIndex: number;
  highlights: HighlightInterface[];
  store: StoreInterface;
  fromHighlightPage?: boolean;
  totalHighlights?: number;
  loadingNewHighlights?: boolean;
  hasFetchedNewHighlights?: boolean;
  fetchNewHighlights?: () => void;
  openItem: (item: ProductItemInterface) => void;
}

const PlayerStage = ({
  show,
  toggle,
  initialHighlightIndex,
  highlights,
  store,
  fromHighlightPage = false,
  totalHighlights,
  loadingNewHighlights = false,
  hasFetchedNewHighlights = false,
  fetchNewHighlights,
  openItem,
}: PlayerStageProps) => {
  const router = useRouter();
  const { cart, toggleModal: toggleCartModal } = CartContextV2.useContainer();
  const { getPaths } = StorefrontContext.useContainer();

  const { modals, toggleModal } = useModals(["products", "store_info", "share_highlight", "all_products"]);
  const [hasInitialized, setHasInitialized] = useState<boolean>(true);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState<number>(initialHighlightIndex);
  const [currentVideoIndex, setCurrentVideoIndex] = useState<number>(0);
  const highlight = highlights[currentHighlightIndex];
  const [playVideosMuted, setPlayVideosMuted] = useState<boolean>(true);

  const allProducts = useMemo(
    () => highlights.flatMap((h) => h.videos.flatMap((v) => v.products)).map((i) => computeItemAvailability(i)),
    [highlights]
  );
  const computedPaths = useMemo(() => getPaths(store), [store]);

  useLockPageScroll(show);

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    loop: false,
    initial: 0,
    slides: {
      origin: "center",
      perView: 1,
      spacing: 0,
    },
    breakpoints: {
      "(min-width: 800px)": {
        slides: {
          origin: "center",
          perView: 3,
          spacing: 0,
        },
      },
      "(min-width: 1280px)": {
        slides: {
          origin: "center",
          perView: 5,
          spacing: 0,
        },
      },
    },
    slideChanged(slider) {
      setCurrentHighlightIndex(slider.track.details.rel);
    },
  });

  useEffect(() => {
    if (show) {
      slider.current?.moveToIdx(initialHighlightIndex);
    } else {
      setTimeout(() => {
        slider.current?.moveToIdx(0);
      }, 300);
    }
  }, [show, initialHighlightIndex, slider]);

  usePreventFirstRun(() => {
    setHasInitialized(false);
    setTimeout(() => {
      slider.current?.update();
      setHasInitialized(true);
    }, 50);
  }, [highlights.length]);

  useEffect(() => {
    if (hasFetchedNewHighlights) {
      setTimeout(() => {
        slider.current?.moveToIdx(1);
        setCurrentVideoIndex(0);
      }, 200);
    }
  }, [hasFetchedNewHighlights]);

  const goToStore = () => {
    if (fromHighlightPage) {
      router.push(computedPaths.home);
    } else {
      toggle();
    }
  };

  return (
    <>
      <div
        ref={sliderRef}
        className="keen-slider highlights-slider h-full max-h-[950px]"
        style={{ "--spacing-top": fromHighlightPage ? `${pxToVh(110)}vh` : "0vh" } as React.CSSProperties}
      >
        {highlights.map((slide, idx) => (
          <div
            key={idx}
            className={classNames(`keen-slider__slide custom-slide flex flex-row items-center justify-center`, {
              active: idx === currentHighlightIndex,
              inactive: idx !== currentHighlightIndex,
            })}
            onClick={() => slider.current?.moveToIdx(idx)}
          >
            <div
              className={classNames(
                `max-w-[90%] md:w-full aspect-[9/16] rounded-15 transition-all duration-300 ease-out cursor-pointer overflow-hidden`,
                {
                  "scale-[0.5]": idx !== currentHighlightIndex,
                  "origin-center": idx === currentHighlightIndex || Math.abs(idx - currentHighlightIndex) === 1,
                  "origin-right": idx < currentHighlightIndex && Math.abs(idx - currentHighlightIndex) !== 1,
                  "origin-left": idx > currentHighlightIndex && Math.abs(idx - currentHighlightIndex) !== 1,
                  "w-[calc(90vh*9/16)]": !fromHighlightPage,
                  "w-[calc(85vh*9/16)]": fromHighlightPage,
                }
              )}
            >
              <HighlightPlayer
                highlight={slide}
                isActive={idx === currentHighlightIndex && show}
                goToNextHighlight={() => slider.current?.moveToIdx(idx + 1)}
                goToPrevHighlight={() => slider.current?.moveToIdx(idx - 1)}
                isFirstHighlight={idx === 0}
                isLastHighlight={idx === highlights.length - 1}
                playVideosMuted={playVideosMuted}
                setPlayVideosMuted={setPlayVideosMuted}
                toggleModal={toggleModal}
                setCurrentVideoIndex={setCurrentVideoIndex}
                currentVideoIndex={currentVideoIndex}
                cart={cart}
                openCart={() => toggleCartModal("cart")}
                hasMoreHighlights={totalHighlights > highlights.length && fromHighlightPage}
                fetchNewHighlights={fetchNewHighlights}
                isLoadingNewHighlights={loadingNewHighlights}
                hide={!hasInitialized && idx !== currentHighlightIndex} //to hide the previews of newly loaded highlights until the slider is updated
                goToStore={goToStore}
                storeName={store.name}
              />
            </div>
          </div>
        ))}
      </div>

      {highlight && highlight?.videos[currentVideoIndex]?.products?.length > 0 && (
        <ProductsModal
          show={modals?.products}
          toggle={() => toggleModal("products")}
          items={highlight.videos[currentVideoIndex].products.map((i) => computeItemAvailability(i))}
          openItem={openItem}
        />
      )}

      {highlights.length > 0 && (
        <ProductsModal
          show={modals?.all_products}
          toggle={() => toggleModal("all_products")}
          items={allProducts}
          openItem={openItem}
        />
      )}
      <StoreInfoModal
        show={modals?.store_info}
        toggle={() => toggleModal("store_info")}
        store={store}
        itemSlug={highlight?.slug}
        shareAction={() => toggleModal("share_highlight")}
      />
      <ShareHighlightModal
        show={modals?.share_highlight}
        toggle={() => toggleModal("share_highlight")}
        slug="test-staging"
      />
    </>
  );
};

export default PlayerStage;

function pxToVh(px) {
  const vh = window.innerHeight;
  return (px / vh) * 100;
}
